import { computed, ref, toValue, watch } from 'vue'

import { rangeOptions } from '@/composables/useAgentOverview'
import api from '@/store/api'

const toDay = date =>
  [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-')

export function useLeadsReport({ endpoint, params = {} }) {
  const rangeKey = ref(30)
  const loading = ref(true)
  const loadingMore = ref(false)
  const error = ref('')
  const report = ref(null)
  const rows = ref([])
  const nextCursor = ref(null)
  let latest = 0

  const dateRange = computed(() => {
    const end = new Date()
    const begin = new Date()
    begin.setDate(end.getDate() - (rangeKey.value - 1))
    return { beginning_date: toDay(begin), ending_date: toDay(end) }
  })

  const path = cursor => {
    const query = new URLSearchParams({ ...toValue(params), ...dateRange.value, cursor: String(cursor) })
    return `${toValue(endpoint)}?${query}`
  }

  const fetchPage = cursor => api.call('GET', path(cursor)).then(response => response?.data ?? {})

  const failed = e => {
    error.value = e?.response?.data?.errors?.[0] || 'Failed to load the report.'
  }

  const load = async () => {
    const request = ++latest
    loading.value = true
    error.value = ''
    try {
      const json = await fetchPage(0)
      if (request !== latest) return
      report.value = json
      rows.value = json.customers?.data ?? []
      nextCursor.value = json.customers?.next_cursor ?? null
    } catch (e) {
      if (request === latest) failed(e)
    } finally {
      if (request === latest) loading.value = false
    }
  }

  const loadMore = async () => {
    if (nextCursor.value === null) return
    const request = latest
    loadingMore.value = true
    try {
      const json = await fetchPage(nextCursor.value)
      if (request !== latest) return
      rows.value = [...rows.value, ...(json.customers?.data ?? [])]
      nextCursor.value = json.customers?.next_cursor ?? null
    } catch (e) {
      if (request === latest) failed(e)
    } finally {
      loadingMore.value = false
    }
  }

  watch(() => path(0), load, { immediate: true })

  return {
    rangeKey,
    rangeOptions,
    loading,
    loadingMore,
    error,
    report,
    rows,
    hasMore: computed(() => nextCursor.value !== null),
    dateRange,
    loadMore
  }
}
